import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { Card, Stack, Container, Typography, TablePagination, IconButton, Tooltip, CircularProgress } from '@mui/material';
import Page from '../../components/Page';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import SortDropdown from '../../components/SortDropdown';
import tracker from '../../api/tracker';
import FreeSelect from '../../components/hook-form/FreeSelect';
import useNotification from '../../hooks/useNotificaction';
import AgGridWrapper, { agDefaultColDef, StatusCell } from '../../components/tables/AgGridWrapper';
import { generateRegistrationPdf } from '../../utils/registrationPdf';

const override = { margin: '0 auto' };

export default function RegistrationIndex() {
  const [registrations, setRegistrations] = useState([]);
  const [sendNotification] = useNotification();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [sort, setSort] = useState('desc');
  const [sortField, setSortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [printingHashid, setPrintingHashid] = useState(null);

  const filterOptions = [
    { value: 'all', label: t('all') },
    { value: 'forStatus(pending)', label: t('pending') },
    { value: 'forStatus(accepted)', label: t('accepted') },
    { value: 'forStatus(rejected)', label: t('rejected') },
  ];

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('registrations', filter, sort, setRegistrations, sortField, setPagination, page, () => {}, filterScope);
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField, filterScope]);

  const onStatusChange = async (status, hash) => {
    await tracker.post(`registrations/${hash}/update`, { status });
    sendNotification({ msg: t('status_updated'), variant: 'success' });
    refreshTable();
  };

  const statusOptions = [
    { value: 'accepted', label: t('accepted') },
    { value: 'pending', label: t('pending') },
    { value: 'rejected', label: t('rejected') },
  ];

  const columnDefs = [
    { field: 'family_name', headerName: t('family_name'), flex: 1, minWidth: 130, filter: 'agTextColumnFilter' },
    { field: 'husband_name', headerName: t('husband_name'), flex: 1, minWidth: 130, sortable: false, filter: 'agTextColumnFilter' },
    { field: 'wife_name', headerName: t('wife_name'), flex: 1, minWidth: 130, sortable: false, filter: 'agTextColumnFilter' },
    { field: 'full_address', headerName: t('full_address'), flex: 1, minWidth: 150, sortable: false, filter: 'agTextColumnFilter' },
    { field: 'phone', headerName: t('phone'), flex: 1, minWidth: 130, sortable: false, filter: 'agTextColumnFilter' },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => <StatusCell value={params.value} t={t} />,
    },
    {
      headerName: t('edit_status'),
      width: 170,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <FreeSelect
          initialValue={params.data.status}
          onChange={(e) => onStatusChange(e.target.value, params.data.hashid)}
          options={statusOptions}
        />
      ),
    },
    {
      headerName: t('actions'),
      width: 140,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => {
        const hid = params.data.hashid;
        const isPrinting = printingHashid === hid;
        return (
          <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Tooltip title={t('view')}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/registration/view/${hid}`); }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('print')}>
              <span>
                <IconButton
                  size="small"
                  disabled={isPrinting}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setPrintingHashid(hid);
                    try {
                      const response = await tracker.get(`registrations/${hid}`);
                      await generateRegistrationPdf(response.data.data);
                    } finally {
                      setPrintingHashid(null);
                    }
                  }}
                >
                  {isPrinting
                    ? <CircularProgress size={14} />
                    : <PrintIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <Page title={t('registrations')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-start" mb={5}>
          <Typography variant="h4" gutterBottom>{t('registrations')}</Typography>
        </Stack>
        <Card>
          <Stack direction="row" alignItems="center" mb={1}>
            <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder={t('search_registrations...')} />
            <SortDropdown sx={{ width: '200px' }} options={filterOptions} sort={filterScope} setSort={setFilterScope} />
          </Stack>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={registrations} columnDefs={columnDefs} defaultColDef={agDefaultColDef} />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
        </Card>
      </Container>
    </Page>
  );
}
